import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog, DialogContent, Box, Typography, Button, IconButton,
  LinearProgress, Chip, Tooltip,
} from '@mui/material';
import {
  Close, ArrowForward, ArrowBack,
  GridViewOutlined, AppsOutlined, PeopleOutlined,
  ListAltOutlined, BuildOutlined, BarChartOutlined,
  LocalOfferOutlined, KeyOutlined, CheckCircleOutlined,
  SchoolOutlined, NavigateNextOutlined,
} from '@mui/icons-material';
import { useThemeMode } from '../contexts/ThemeContext';

interface TutorialStep {
  id: string;
  title: string;
  description: string;
  Icon: React.ElementType;
  color: string;
  highlights: { icon: string; text: string }[];
  path: string | null;
  ctaLabel?: string;
  roles: string[];
}

const ALL_STEPS: TutorialStep[] = [
  {
    id: 'welcome',
    title: 'Welcome to LLM Wrapper',
    description: 'Your unified control panel for managing AI API access across your organisation. This quick tour will walk you through every feature available to your role.',
    Icon: SchoolOutlined,
    color: '#FF1A50',
    highlights: [],
    path: null,
    roles: ['super_admin', 'admin', 'viewer', 'app_viewer'],
  },
  {
    id: 'dashboard',
    title: 'Dashboard',
    description: 'Your home base. See real-time token consumption, active application count, and overall system health at a glance.',
    Icon: GridViewOutlined,
    color: '#3B82F6',
    highlights: [
      { icon: '📊', text: 'Live usage metrics across all apps' },
      { icon: '⚡', text: 'Active application count' },
      { icon: '🟢', text: 'System health status' },
    ],
    path: '/',
    ctaLabel: 'Explore Dashboard',
    roles: ['super_admin', 'admin', 'viewer', 'app_viewer'],
  },
  {
    id: 'apps',
    title: 'Applications',
    description: 'Register and manage AI-powered applications. Each app gets its own auth key, token limits, and endpoint permissions.',
    Icon: AppsOutlined,
    color: '#8B5CF6',
    highlights: [
      { icon: '🔑', text: 'Unique auth key per application' },
      { icon: '🎛️', text: 'Per-app token limits (input & output)' },
      { icon: '🛡️', text: 'Fine-grained endpoint permissions' },
    ],
    path: '/apps',
    ctaLabel: 'Explore Applications',
    roles: ['super_admin', 'admin', 'viewer'],
  },
  {
    id: 'apps_viewer',
    title: 'Your Applications',
    description: 'View the applications you have been granted access to. Monitor their usage and check current status.',
    Icon: AppsOutlined,
    color: '#8B5CF6',
    highlights: [
      { icon: '👁️', text: 'View your assigned applications' },
      { icon: '📈', text: 'Monitor token usage per app' },
      { icon: '✅', text: 'Check active/inactive status' },
    ],
    path: '/apps',
    ctaLabel: 'View Your Apps',
    roles: ['app_viewer'],
  },
  {
    id: 'auth_keys',
    title: 'Auth Keys (Super Admin)',
    description: 'As Super Admin you can reveal any application\'s secret auth key directly from its detail view. Keys are shown masked and are never stored in the browser.',
    Icon: KeyOutlined,
    color: '#F59E0B',
    highlights: [
      { icon: '🔐', text: 'Generated once at registration' },
      { icon: '👁️', text: 'Reveal & copy from app details' },
      { icon: '🚫', text: 'Never stored in browser storage' },
    ],
    path: '/apps',
    ctaLabel: 'Go to Applications',
    roles: ['super_admin'],
  },
  {
    id: 'analytics',
    title: 'Analytics',
    description: 'Dive deep into token consumption trends, per-model breakdowns, and cost estimates across your entire platform.',
    Icon: BarChartOutlined,
    color: '#22C55E',
    highlights: [
      { icon: '📉', text: 'Token usage over time' },
      { icon: '🤖', text: 'Per-model consumption breakdown' },
      { icon: '💰', text: 'Cost tracking & estimates' },
    ],
    path: '/analytics',
    ctaLabel: 'Open Analytics',
    roles: ['super_admin', 'admin'],
  },
  {
    id: 'users',
    title: 'User Management',
    description: 'Invite admins, assign roles, and manage who can access what. Super Admins can also configure fine-grained permissions per user.',
    Icon: PeopleOutlined,
    color: '#EC4899',
    highlights: [
      { icon: '✉️', text: 'Invite users by email' },
      { icon: '🎭', text: 'Assign roles: Admin, Viewer, App Viewer' },
      { icon: '⚙️', text: 'Configure individual permissions' },
    ],
    path: '/users',
    ctaLabel: 'Manage Users',
    roles: ['super_admin', 'admin'],
  },
  {
    id: 'pricing',
    title: 'Pricing',
    description: 'Configure per-model pricing so the platform can track real costs and help you plan budgets for each application.',
    Icon: LocalOfferOutlined,
    color: '#06B6D4',
    highlights: [
      { icon: '💲', text: 'Set per-token rates per model' },
      { icon: '📋', text: 'Reflected in Analytics cost view' },
      { icon: '📌', text: 'Helps budget app token allocations' },
    ],
    path: '/pricing',
    ctaLabel: 'View Pricing',
    roles: ['super_admin', 'admin'],
  },
  {
    id: 'logs',
    title: 'Logs',
    description: 'Inspect every API call in real time — model used, token count, latency, status code, and more. Great for debugging.',
    Icon: ListAltOutlined,
    color: '#F97316',
    highlights: [
      { icon: '📜', text: 'Full request & response logs' },
      { icon: '🔍', text: 'Filter by app, model, or status' },
      { icon: '🐛', text: 'Debug failed or slow requests' },
    ],
    path: '/logs',
    ctaLabel: 'View Logs',
    roles: ['super_admin', 'admin', 'viewer'],
  },
  {
    id: 'operations',
    title: 'Operations',
    description: 'Perform system-level administrative actions like resetting token counters and running maintenance tasks.',
    Icon: BuildOutlined,
    color: '#6366F1',
    highlights: [
      { icon: '🔄', text: 'Reset token usage counters' },
      { icon: '🧪', text: 'Run load tests' },
      { icon: '🛠️', text: 'System maintenance controls' },
    ],
    path: '/operations',
    ctaLabel: 'View Operations',
    roles: ['super_admin'],
  },
  {
    id: 'done',
    title: "You're all set!",
    description: "You know your way around. Dive in and start exploring. You can restart this tour anytime by clicking the graduation cap icon in the sidebar.",
    Icon: CheckCircleOutlined,
    color: '#22C55E',
    highlights: [],
    path: null,
    roles: ['super_admin', 'admin', 'viewer', 'app_viewer'],
  },
];

// ─── Floating pill shown while minimized ─────────────────────────────────────
interface PillProps {
  step: number;
  total: number;
  title: string;
  color: string;
  onExpand: () => void;
  onClose: () => void;
  isDark: boolean;
}

const TutorialPill: React.FC<PillProps> = ({ step, total, title, color, onExpand, onClose, isDark }) => (
  <Box
    sx={{
      position: 'fixed',
      bottom: 24,
      right: 24,
      zIndex: 1400,
      display: 'flex',
      alignItems: 'center',
      gap: 1,
      bgcolor: isDark ? '#1A1A1A' : '#FFFFFF',
      border: `1px solid ${color}44`,
      borderLeft: `3px solid ${color}`,
      borderRadius: '10px',
      px: 1.5,
      py: 1,
      boxShadow: `0 4px 20px rgba(0,0,0,${isDark ? '0.4' : '0.12'})`,
      animation: 'pillPop 0.3s cubic-bezier(0.34,1.56,0.64,1)',
      '@keyframes pillPop': {
        from: { opacity: 0, transform: 'scale(0.8) translateY(10px)' },
        to:   { opacity: 1, transform: 'scale(1) translateY(0)' },
      },
    }}
  >
    <SchoolOutlined sx={{ fontSize: 16, color, flexShrink: 0 }} />
    <Box>
      <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: isDark ? '#EFEFEF' : '#0A0A0A', lineHeight: 1.2 }}>
        Tutorial · Step {step}/{total}
      </Typography>
      <Typography sx={{ fontSize: '0.67rem', color: isDark ? '#888' : '#888', lineHeight: 1.2 }}>
        {title}
      </Typography>
    </Box>
    <Tooltip title="Continue tutorial" arrow>
      <IconButton size="small" onClick={onExpand}
        sx={{ ml: 0.5, bgcolor: `${color}18`, color, '&:hover': { bgcolor: `${color}30` }, p: 0.5 }}>
        <NavigateNextOutlined sx={{ fontSize: 16 }} />
      </IconButton>
    </Tooltip>
    <Tooltip title="Close tutorial" arrow>
      <IconButton size="small" onClick={onClose}
        sx={{ color: isDark ? '#555' : '#AAA', '&:hover': { color: '#EF4444' }, p: 0.25 }}>
        <Close sx={{ fontSize: 14 }} />
      </IconButton>
    </Tooltip>
  </Box>
);

// ─── Main component ───────────────────────────────────────────────────────────
export type TutorialState = 'closed' | 'open' | 'minimized';

interface Props {
  state: TutorialState;
  step: number;
  role: string;
  onClose: () => void;
  onMinimize: () => void;
  onExpand: () => void;
  onStepChange: (step: number) => void;
}

const TutorialModal: React.FC<Props> = ({
  state, step, role, onClose, onMinimize, onExpand, onStepChange,
}) => {
  const { mode } = useThemeMode();
  const navigate = useNavigate();
  const isDark = mode === 'dark';

  const steps = ALL_STEPS.filter(s => s.roles.includes(role));
  const current = steps[step] ?? steps[0];
  const isLast  = step === steps.length - 1;
  const isFirst = step === 0;
  const progress = ((step + 1) / steps.length) * 100;

  const [animKey, setAnimKey]         = useState(0);
  const [animDir, setAnimDir]         = useState<'forward' | 'back'>('forward');
  const [highlightIn, setHighlightIn] = useState(false);

  const bg     = isDark ? '#111111' : '#FFFFFF';
  const border = isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.07)';
  const textP  = isDark ? '#EFEFEF' : '#0A0A0A';
  const textS  = isDark ? '#888888' : '#666666';

  // Stagger highlight items on step change
  useEffect(() => {
    setHighlightIn(false);
    const t = setTimeout(() => setHighlightIn(true), 120);
    return () => clearTimeout(t);
  }, [step]);

  const goNext = useCallback(() => {
    if (isLast) { onClose(); return; }
    setAnimDir('forward');
    setAnimKey(k => k + 1);
    onStepChange(step + 1);
  }, [isLast, step, onClose, onStepChange]);

  const goBack = useCallback(() => {
    if (isFirst) return;
    setAnimDir('back');
    setAnimKey(k => k + 1);
    onStepChange(step - 1);
  }, [isFirst, step, onStepChange]);

  const handleExplore = () => {
    if (current.path) {
      navigate(current.path);
      onMinimize();
    }
  };

  // Keyboard nav
  useEffect(() => {
    if (state !== 'open') return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') goNext();
      else if (e.key === 'ArrowLeft') goBack();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [state, goNext, goBack]);

  const slideIn = animDir === 'forward'
    ? 'tutSlideRight 0.22s ease'
    : 'tutSlideLeft 0.22s ease';

  return (
    <>
      {/* ── Full modal ─────────────────────────────────────────────── */}
      <Dialog
        open={state === 'open'}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        slotProps={{
          paper: {
            sx: {
              borderRadius: '16px',
              overflow: 'hidden',
              bgcolor: bg,
              boxShadow: isDark
                ? '0 24px 64px rgba(0,0,0,0.7)'
                : '0 24px 64px rgba(0,0,0,0.15)',
            },
          },
        }}
        sx={{
          '& .MuiBackdrop-root': { backdropFilter: 'blur(4px)', bgcolor: 'rgba(0,0,0,0.5)' },
          '@keyframes tutSlideRight': {
            from: { opacity: 0, transform: 'translateX(28px)' },
            to:   { opacity: 1, transform: 'translateX(0)' },
          },
          '@keyframes tutSlideLeft': {
            from: { opacity: 0, transform: 'translateX(-28px)' },
            to:   { opacity: 1, transform: 'translateX(0)' },
          },
          '@keyframes iconBounce': {
            '0%':   { transform: 'scale(0.6)', opacity: 0 },
            '60%':  { transform: 'scale(1.15)' },
            '80%':  { transform: 'scale(0.95)' },
            '100%': { transform: 'scale(1)', opacity: 1 },
          },
          '@keyframes highlightSlide': {
            from: { opacity: 0, transform: 'translateX(-12px)' },
            to:   { opacity: 1, transform: 'translateX(0)' },
          },
        }}
      >
        {/* Coloured header */}
        <Box sx={{
          background: `linear-gradient(135deg, ${current.color}1A 0%, ${current.color}08 100%)`,
          borderBottom: `1px solid ${current.color}2A`,
          pt: 3, pb: 2.5, px: 3,
          position: 'relative',
          textAlign: 'center',
        }}>
          {/* Close */}
          <IconButton
            onClick={onClose}
            size="small"
            sx={{ position: 'absolute', top: 12, right: 12, color: textS, '&:hover': { color: '#EF4444' } }}
          >
            <Close sx={{ fontSize: 16 }} />
          </IconButton>

          {/* Step badge */}
          <Chip
            label={`${step + 1} / ${steps.length}`}
            size="small"
            sx={{
              position: 'absolute', top: 14, left: 14,
              bgcolor: `${current.color}18`, color: current.color,
              fontWeight: 700, fontSize: '0.7rem', height: 22, border: 'none',
            }}
          />

          {/* Animated icon */}
          <Box
            key={`icon-${animKey}`}
            sx={{
              width: 68, height: 68, borderRadius: '18px',
              bgcolor: `${current.color}18`,
              border: `1px solid ${current.color}30`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              mx: 'auto', mb: 1.5,
              animation: 'iconBounce 0.4s cubic-bezier(0.34,1.56,0.64,1)',
            }}
          >
            <current.Icon sx={{ fontSize: 32, color: current.color }} />
          </Box>
        </Box>

        {/* Step content */}
        <DialogContent sx={{ px: 3, py: 2.5 }}>
          <Box key={animKey} sx={{ animation: `${slideIn}` }}>
            <Typography sx={{
              fontWeight: 700, fontSize: '1.1rem', color: textP,
              letterSpacing: '-0.02em', mb: 1, lineHeight: 1.3,
            }}>
              {current.title}
            </Typography>
            <Typography sx={{ fontSize: '0.875rem', color: textS, lineHeight: 1.6, mb: 2 }}>
              {current.description}
            </Typography>

            {/* Highlights */}
            {current.highlights.length > 0 && (
              <Box sx={{
                display: 'flex', flexDirection: 'column', gap: 1,
                bgcolor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
                border: `1px solid ${border}`,
                borderRadius: '10px',
                p: 1.5,
              }}>
                {current.highlights.map((h, i) => (
                  <Box
                    key={h.text}
                    sx={{
                      display: 'flex', alignItems: 'center', gap: 1.5,
                      opacity: highlightIn ? 1 : 0,
                      animation: highlightIn
                        ? `highlightSlide 0.2s ease ${i * 60}ms both`
                        : 'none',
                    }}
                  >
                    <Box sx={{
                      width: 30, height: 30, borderRadius: '8px', flexShrink: 0,
                      bgcolor: `${current.color}14`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: '0.9rem',
                    }}>
                      {h.icon}
                    </Box>
                    <Typography sx={{ fontSize: '0.82rem', color: textS, lineHeight: 1.4 }}>
                      {h.text}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}

            {/* "Done" step — special celebration */}
            {current.id === 'done' && (
              <Box sx={{ textAlign: 'center', pt: 1 }}>
                <Typography sx={{ fontSize: '2rem', mb: 0.5 }}>🎉</Typography>
                <Typography sx={{ fontSize: '0.8rem', color: textS }}>
                  Use the <strong style={{ color: textP }}>graduation cap</strong> button in the sidebar to reopen this guide anytime.
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>

        {/* Progress + actions */}
        <Box sx={{ px: 3, pb: 2.5 }}>
          {/* Progress bar */}
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{
              mb: 2, height: 3, borderRadius: 2,
              bgcolor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)',
              '& .MuiLinearProgress-bar': {
                bgcolor: current.color,
                borderRadius: 2,
                transition: 'transform 0.4s ease',
              },
            }}
          />

          {/* Step dots */}
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5, mb: 2 }}>
            {steps.map((_, i) => (
              <Box
                key={i}
                onClick={() => { setAnimDir(i > step ? 'forward' : 'back'); setAnimKey(k => k + 1); onStepChange(i); }}
                sx={{
                  width: i === step ? 20 : 6,
                  height: 6,
                  borderRadius: 3,
                  bgcolor: i === step ? current.color : (isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.1)'),
                  cursor: 'pointer',
                  transition: 'all 0.25s ease',
                  '&:hover': { bgcolor: i === step ? current.color : (isDark ? 'rgba(255,255,255,0.25)' : 'rgba(0,0,0,0.2)') },
                }}
              />
            ))}
          </Box>

          {/* Buttons */}
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Button
              size="small"
              onClick={goBack}
              disabled={isFirst}
              startIcon={<ArrowBack sx={{ fontSize: 14 }} />}
              sx={{
                color: textS, fontSize: '0.8rem', textTransform: 'none',
                borderColor: border, border: '1px solid',
                '&:hover': { borderColor: current.color, color: current.color },
                '&.Mui-disabled': { opacity: 0.3 },
                px: 1.5,
              }}
            >
              Back
            </Button>

            <Box sx={{ flex: 1 }} />

            {current.path && (
              <Button
                size="small"
                variant="outlined"
                onClick={handleExplore}
                endIcon={<NavigateNextOutlined sx={{ fontSize: 14 }} />}
                sx={{
                  fontSize: '0.8rem', textTransform: 'none',
                  borderColor: current.color, color: current.color,
                  '&:hover': { bgcolor: `${current.color}12`, borderColor: current.color },
                  px: 1.5,
                }}
              >
                {current.ctaLabel ?? 'Explore'}
              </Button>
            )}

            <Button
              size="small"
              variant="contained"
              onClick={goNext}
              endIcon={!isLast && <ArrowForward sx={{ fontSize: 14 }} />}
              sx={{
                fontSize: '0.8rem', textTransform: 'none',
                bgcolor: current.color,
                '&:hover': { bgcolor: current.color, filter: 'brightness(1.1)' },
                px: 2,
                boxShadow: `0 2px 12px ${current.color}44`,
              }}
            >
              {isLast ? 'Finish' : 'Next'}
            </Button>
          </Box>

          {/* Keyboard hint */}
          <Typography sx={{ fontSize: '0.67rem', color: isDark ? '#444' : '#CCC', textAlign: 'center', mt: 1.5 }}>
            Use ← → arrow keys to navigate
          </Typography>
        </Box>
      </Dialog>

      {/* ── Floating pill (minimized) ───────────────────────────────── */}
      {state === 'minimized' && (
        <TutorialPill
          step={step + 1}
          total={steps.length}
          title={current.title}
          color={current.color}
          onExpand={onExpand}
          onClose={onClose}
          isDark={isDark}
        />
      )}
    </>
  );
};

export default TutorialModal;
